<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import {
  ModuleUpsertDialogDataQuery,
  ModuleUpsertDialogDataQueryVariables,
} from '@/generated/graphql'
import EditDialog from '@/components/dialogs/EditDialog.vue'
import TextField from '@/components/input/TextField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import useAsyncDialog from '@/components/dialogs/useAsyncDialog'
import { abbreviation, normalDescription, normalName, required, unique } from '@/validation'

export type ModuleFields = {
  id?: string
  name: string
  abbreviation: string
  description?: string
}

const props = defineProps<{
  bopId: string
  title?: string
}>()

const { t } = useI18n()

const fetchQuery = useQuery<ModuleUpsertDialogDataQuery, ModuleUpsertDialogDataQueryVariables>(
  gql`
    query ModuleUpsertDialogData($bopId: ID!) {
      product {
        bop(id: $bopId) {
          id
          nodes {
            id
            module {
              id
              abbreviation
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId }),
)

type Model = Partial<ModuleFields>
const model = reactive<Model>({})
const takenAbbreviations = computed(() =>
  (fetchQuery.result.value?.product.bop.nodes ?? [])
    .map((n) => n.module)
    .filter((m) => m.id != model.id)
    .map((m) => m.abbreviation),
)
const validation = useVuelidate<Model>(
  {
    name: { required, normalName },
    abbreviation: { required, abbreviation, unique: unique(takenAbbreviations) },
    description: { normalDescription },
  },
  model,
)

const isCreate = ref(true)
const { openAsyncDialog, asyncDialogResolve, asyncDialogLoading, asyncDialogIsOpen } =
  useAsyncDialog<() => Promise<void>>()

const open = async (options: {
  create?: boolean
  initial?: Model
  save: (fields: ModuleFields) => Promise<unknown>
}): Promise<unknown> => {
  isCreate.value = options.create ?? false
  model.id = options.initial?.id
  model.name = options.initial?.name
  model.abbreviation = options.initial?.abbreviation
  model.description = options.initial?.description
  validation.value.$reset()
  saveFields.value = options.save
  return openAsyncDialog()
}

const saveFields = ref<(fields: ModuleFields) => Promise<unknown>>()
const save = () => {
  if (!asyncDialogResolve.value) {
    return
  }

  const fields = { ...model } as ModuleFields
  asyncDialogResolve.value(async () => {
    await saveFields.value?.(fields)
  })
}

defineExpose({ open })
</script>

<template>
  <edit-dialog
    v-model="asyncDialogIsOpen"
    :create="isCreate"
    :entity="t('entity.module.singular')"
    :validation="validation"
    :save-loading="asyncDialogLoading"
    @save="save"
  >
    <template v-if="props.title" #title>{{ props.title }}</template>
    <slot />
    <text-field
      v-model="model.name"
      :label="t('entity.module.field.name')"
      required
      :validation="validation.name"
    />
    <text-field
      v-model="model.abbreviation"
      :label="t('entity.module.field.abbreviation')"
      required
      :validation="validation.abbreviation"
    />
    <text-area-field
      v-model="model.description"
      :label="t('entity.module.field.description')"
      :validation="validation.description"
    />
  </edit-dialog>
</template>

<style scoped lang="scss"></style>
